import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Droplets, Timer, Target, FileText, Save } from 'lucide-react';
import { LloroFormData } from '../../types';

interface LloroFormProps {
  onSubmit: (data: LloroFormData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<LloroFormData>;
  initialDate?: Date;
}

const intensityOptions = [
  { value: 'leve', label: 'Leve', emoji: '😢', color: '#22c55e', description: 'Llanto suave, lágrimas ligeras' },
  { value: 'moderado', label: 'Moderado', emoji: '😭', color: '#f59e0b', description: 'Llanto notable, con sollozos' },
  { value: 'fuerte', label: 'Fuerte', emoji: '😩', color: '#ef4444', description: 'Llanto intenso, muy emotivo' },
];

const durationOptions = [
  { value: 'menos de 1 minuto', label: 'Menos de 1 min' },
  { value: '1-5 minutos', label: '1-5 minutos' },
  { value: '5-15 minutos', label: '5-15 minutos' },
  { value: '15-30 minutos', label: '15-30 minutos' },
  { value: '30-60 minutos', label: '30-60 minutos' },
  { value: 'más de 1 hora', label: 'Más de 1 hora' },
];

export const LloroForm: React.FC<LloroFormProps> = ({
  onSubmit,
  onCancel,
  initialData = {},
  initialDate,
}) => {
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    datetime: formatDateForInput(initialDate || new Date()),
    intensity: initialData.intensity || '',
    duration: initialData.duration || '',
    trigger_cause: initialData.trigger_cause || '',
    notes: initialData.notes || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialDate) {
      setFormData(prev => ({
        ...prev,
        datetime: formatDateForInput(initialDate),
      }));
    }
  }, [initialDate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.intensity) {
      newErrors.intensity = 'La intensidad es obligatoria';
    }

    if (!formData.datetime) {
      newErrors.datetime = 'La fecha y hora son obligatorias';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const [date, time] = formData.datetime.split('T');
      const submitData: LloroFormData = {
        intensity: formData.intensity as 'leve' | 'moderado' | 'fuerte',
        duration: formData.duration || undefined,
        trigger_cause: formData.trigger_cause || undefined,
        notes: formData.notes || undefined,
        date,
        time,
      };

      await onSubmit(submitData);
    } catch (err: any) {
      setErrors({ submit: err.message || 'Error al guardar' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedIntensity = intensityOptions.find(o => o.value === formData.intensity);

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pb-4">
      {errors.submit && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm"
        >
          {errors.submit}
        </motion.div>
      )}

      {/* Fecha y hora */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          <Clock size={16} className="inline mr-2" />
          Fecha y hora *
        </label>
        <input
          type="datetime-local"
          value={formData.datetime}
          onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
          className={`input ${errors.datetime ? 'border-red-400' : ''}`}
          required
        />
        {errors.datetime && <p className="text-red-500 text-xs mt-1">{errors.datetime}</p>}
      </div>

      {/* Intensidad */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-3">
          <Droplets size={16} className="inline mr-2" />
          Intensidad *
        </label>
        <div className="grid grid-cols-3 gap-3">
          {intensityOptions.map((option) => (
            <motion.button
              key={option.value}
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={() => setFormData({ ...formData, intensity: option.value })}
              className={`p-4 rounded-2xl border-2 transition-all ${
                formData.intensity === option.value
                  ? 'border-current'
                  : 'border-transparent bg-[var(--color-surface)]'
              }`}
              style={{
                borderColor: formData.intensity === option.value ? option.color : undefined,
                backgroundColor: formData.intensity === option.value ? `${option.color}15` : undefined,
              }}
            >
              <span className="text-3xl block mb-2">{option.emoji}</span>
              <span className="text-sm font-medium" style={{ color: formData.intensity === option.value ? option.color : 'var(--color-text)' }}>
                {option.label}
              </span>
            </motion.button>
          ))}
        </div>
        {errors.intensity && <p className="text-red-500 text-xs mt-1">{errors.intensity}</p>}

        {/* Info de intensidad */}
        {selectedIntensity && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-3 rounded-xl border-l-4"
            style={{ borderLeftColor: selectedIntensity.color, backgroundColor: `${selectedIntensity.color}10` }}
          >
            <p className="font-semibold text-sm flex items-center gap-2" style={{ color: selectedIntensity.color }}>
              {selectedIntensity.emoji} {selectedIntensity.label}
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">{selectedIntensity.description}</p>
          </motion.div>
        )}
      </div>

      {/* Duración */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          <Timer size={16} className="inline mr-2" />
          Duración (opcional)
        </label>
        <select
          value={formData.duration}
          onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
          className="input"
        >
          <option value="">Seleccionar...</option>
          {durationOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Causa/Motivo */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          <Target size={16} className="inline mr-2" />
          Causa o motivo (opcional)
        </label>
        <input
          type="text"
          value={formData.trigger_cause}
          onChange={(e) => setFormData({ ...formData, trigger_cause: e.target.value })}
          className="input"
          placeholder="Ej: estrés, tristeza, película, alegría..."
          maxLength={255}
        />
      </div>

      {/* Notas */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
          <FileText size={16} className="inline mr-2" />
          Notas (opcional)
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="input min-h-[100px] resize-none"
          placeholder="Cómo te sentiste, qué lo provocó, cómo te sentiste después..."
          maxLength={500}
        />
        <p className="text-xs text-[var(--color-text-muted)] mt-1 text-right">
          {formData.notes.length}/500 caracteres
        </p>
      </div>

      {/* Botones */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 py-4 rounded-xl border-2 border-[var(--color-border)] text-[var(--color-text)] font-medium hover:bg-[var(--color-surface)] transition-colors"
        >
          Cancelar
        </button>
        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileTap={{ scale: 0.98 }}
          className="flex-1 btn btn-primary py-4"
        >
          {isSubmitting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Save size={20} />
              Guardar
            </>
          )}
        </motion.button>
      </div>
    </form>
  );
};
